import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.js";
import AdSenseUnit from "./AdSenseUnit.jsx";
import BadgeToast from "./BadgeToast.jsx";
import QuizCard from "./QuizCard.jsx";
import ReportQuestionButton from "./ReportQuestionButton.jsx";

const PUBLIC_BASE_URL =
  import.meta.env.VITE_PUBLIC_BASE_URL || "https://quizfreak.com";

const ClipboardIcon = ({ copied = false }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {copied ? (
      <path d="M20 6 9 17l-5-5" />
    ) : (
      <>
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 5H7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      </>
    )}
  </svg>
);


const ResultPanel = ({ quiz, result, relatedQuizzes = [], onRestart }) => {
  const { user } = useAuth();
  const [shareStatus, setShareStatus] = useState("idle");

  // Referral-aware quiz URL: append ?ref=<username> for authenticated users
  // so friends who click the link are tracked as referred visitors. Copy link
  // uses this URL; Facebook share uses the score-specific OG URL for the
  // rich preview (those aren't the right place for a ref param).
  const quizUrl = user?.username
    ? `${PUBLIC_BASE_URL}/v2/quiz/${quiz?.slug || ""}/?ref=${encodeURIComponent(user.username)}`
    : `${PUBLIC_BASE_URL}/v2/quiz/${quiz?.slug || ""}/`;

  // Use breakdown from API score response — has correct answers revealed post-submission
  const questionSummary = useMemo(() => {
    if (!result?.breakdown?.length) return [];
    return result.breakdown.map((item) => ({
      id: item.question_id,
      prompt: item.prompt,
      userText: item.user_choice_text,
      correctText: item.correct_choice_text,
      isCorrect: item.is_correct,
      skipped: item.user_choice_id == null,
    }));
  }, [result]);

  const isPersonality = result?.isPersonality === true;

  const scoreShareUrl = useMemo(() => {
    if (!quiz?.slug) return quizUrl;
    if (isPersonality) {
      // Result-specific OG page so the Facebook card shows which outcome the
      // user got (e.g. 'I got "The Emotional Alchemist" on ...'). Falls back
      // to the plain quiz URL if the result key is missing.
      if (!result?.personalityResultKey) return quizUrl;
      const personalityShareUrl = `${PUBLIC_BASE_URL}/v2/share/${quiz.slug}/personality/${encodeURIComponent(result.personalityResultKey)}/`;
      // Carry the referral param through the share page (the backend redirect
      // preserves the query string) so a friend clicking the Facebook card
      // still triggers the referral-start flow, as the plain quizUrl did before.
      return user?.username
        ? `${personalityShareUrl}?ref=${encodeURIComponent(user.username)}`
        : personalityShareUrl;
    }
    if (!result?.stats) return quizUrl;
    const { percent = 0, passed = false, score = 0, total = 0 } = result.stats;
    const variant = percent === 100 ? "aced" : passed ? "pass" : "fail";
    return `${PUBLIC_BASE_URL}/v2/share/${quiz.slug}/${variant}/${score}/${total}/`;
  }, [quiz?.slug, result, quizUrl, isPersonality, user?.username]);

  const facebookShareUrl = useMemo(
    () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(scoreShareUrl)}`,
    [scoreShareUrl],
  );

  const fireShareEvent = (platform) => {
    try {
      window.quizfreakAnalytics?.track("share_click", {
        quiz_slug: quiz?.slug,
        quiz_title: quiz?.title,
        platform,
        score: result?.stats?.score,
        total: result?.stats?.total,
        passed: result?.stats?.passed,
      });
    } catch (_) {
      // no-op until analytics is wired
    }
  };

  const handleShareFacebook = () => {
    fireShareEvent("facebook");
    if (typeof window !== "undefined") {
      window.open(facebookShareUrl, "share-window", "width=600,height=400");
    }
  };

  const handleCopyLink = async () => {
    try {
      // Copy the referral-aware quiz URL (includes ?ref= for logged-in users)
      // so friends land on the quiz and credit the sharer. Facebook share
      // still uses the OG score URL for the rich social preview.
      await navigator.clipboard.writeText(quizUrl);
      fireShareEvent("copy_link");
      setShareStatus("copied");
      setTimeout(() => setShareStatus("idle"), 2000);
    } catch (error) {
      console.error("Clipboard copy failed", error);
      setShareStatus("error");
    }
  };

  if (!result) return null;

  const passBadgeClasses = result.stats?.passed
    ? "bg-green-500/20 text-green-300 border border-green-500/40"
    : "bg-red-500/20 text-red-300 border border-red-500/40";

  const relatedSuggestions = useMemo(() => {
    if (!relatedQuizzes?.length || !quiz) return [];
    const filtered = relatedQuizzes.filter((item) => item.slug !== quiz.slug);
    const sameCategory = filtered.filter((item) => item.category === quiz.category);
    const others = filtered.filter((item) => item.category !== quiz.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [relatedQuizzes, quiz]);

  return (
    <>
    <BadgeToast badges={result.badgesEarned} />
    <div className="space-y-6">

      {/* Anonymous upsell — shown above the scorecard for guests only.
          Suppressed for personality results: personality submissions
          deliberately omit ranks, scores, badges, and redeemable points,
          so the "Save Your Rank" / "Climb the Leaderboard" copy would
          promise leaderboard state that doesn't exist for this quiz type. */}
      {!user && !isPersonality && (
        <div className="rounded-3xl border border-amber-400/30 bg-gradient-to-b from-amber-500/10 to-transparent p-6 shadow-lg shadow-black/40">
          <h2 className="text-center text-xl font-bold text-white">
            🏆 Unlock Your Full Quizfreak Profile
          </h2>
          <ul className="mt-5 space-y-3">
            {[
              ["📍", "Save Your Rank"],
              ["📈", "Track Your Progress"],
              ["🥇", "Unlock Badges"],
              ["🏅", "Climb the Leaderboard"],
              ["▶️",  "Continue Where You Left Off"],
            ].map(([icon, label]) => (
              <li key={label} className="flex items-center gap-3 text-sm text-white/90">
                <span className="text-lg leading-none">{icon}</span>
                <span className="font-medium">{label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-center text-xs font-medium text-amber-300/80">
            ⚠️ Your progress will not be saved
          </p>
          <Link
            to="/register"
            className="mt-4 flex w-full items-center justify-center rounded-full bg-amber-400 py-3 text-sm font-bold text-slate-900 shadow-md shadow-amber-900/30 transition hover:bg-amber-300"
          >
            Save My Rank
          </Link>
        </div>
      )}

      <div className="rounded-3xl border border-white/10 bg-slate-900/80 p-6 shadow-lg shadow-black/50">
        {isPersonality ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm uppercase tracking-[0.3em] text-violet-300/70">Your Result</p>
            <h1 className="text-4xl font-bold text-white">{result.title}</h1>
            <p className="max-w-md text-base text-white/70">{result.body}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 text-center md:flex-row md:items-center md:justify-between md:text-left">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-white/60">Scorecard</p>
              <h1 className="mt-2 text-3xl font-semibold text-white">{quiz?.title}</h1>
            </div>
            <div className="flex flex-col items-center justify-center">
              <p className="text-5xl font-bold text-white whitespace-nowrap">
                {result.stats.score} / {result.stats.total}
              </p>
              <p className="text-lg text-white/70">{result.stats.percent}%</p>
            </div>
            <div className="flex flex-col items-center gap-2 md:items-end">
              <span className={`inline-flex items-center rounded-full px-4 py-1 text-sm font-semibold ${passBadgeClasses}`}>
                {result.stats.passed ? "PASS" : "FAIL"}
              </span>
              <p className="text-xl font-semibold text-white">{result.title}</p>
              <p className="max-w-sm text-sm text-white/70">{result.body}</p>
            </div>
          </div>
        )}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={handleShareFacebook}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1877F2] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition hover:bg-[#1565C0]"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
              <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
            </svg>
            Share on Facebook
          </button>
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
          >
            <ClipboardIcon copied={shareStatus === "copied"} />
            {shareStatus === "copied" ? "Link copied!" : "Copy link"}
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white/80 hover:border-white/60"
          >
            Take it again
          </button>
        </div>
        {shareStatus === "error" && (
          <p className="mt-2 text-xs text-red-300">
            Couldn't copy automatically — copy this link instead: {quizUrl}
          </p>
        )}

        {/* Authenticated: show points earned */}
        {user && result.pointsEarned != null && (
          <div className="mt-5 flex items-center gap-3 rounded-2xl border border-brand-glow/30 bg-brand-glow/10 px-4 py-3">
            <span className="text-2xl font-bold text-brand-glow">+{result.pointsEarned}</span>
            <div>
              <p className="text-sm font-semibold text-white">
                {result.pointsEarned > 0 ? "Points earned" : "No new points"}
              </p>
              <p className="text-xs text-white/60">
                {result.attemptNumber > 1
                  ? `Attempt #${result.attemptNumber} · `
                  : ""}
                {result.totalPoints} pts total
              </p>
            </div>
          </div>
        )}

        {/* Anonymous: sign-up CTA */}
        {!user && result.pointsWouldEarn != null && (
          <div className="mt-5 flex flex-col gap-2 rounded-2xl border border-brand-glow/30 bg-brand-glow/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-white">
              <span className="font-semibold text-brand-glow">+{result.pointsWouldEarn} pts</span>{" "}
              waiting — create an account to save your score
            </p>
            <Link
              to="/register"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-brand-glow px-4 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
            >
              Sign up &amp; claim
            </Link>
          </div>
        )}
      </div>

      <AdSenseUnit />

      {isPersonality ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <p className="text-sm uppercase tracking-[0.3em] text-white/60">Your answers</p>
          <ul className="mt-4 space-y-3">
            {questionSummary.map((item) => (
              <li key={item.id} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                <p className="font-semibold text-white">{item.prompt}</p>
                <p className="mt-1 text-sm text-violet-300">
                  {item.skipped ? "Skipped" : item.userText}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <p className="text-sm uppercase tracking-[0.3em] text-white/60">Question breakdown</p>
          <ul className="mt-4 space-y-4">
            {questionSummary.map((item) => (
              <li
                key={item.id}
                className={`rounded-2xl border p-4 ${
                  item.skipped
                    ? "border-white/10 bg-slate-950/40"
                    : item.isCorrect
                    ? "border-green-500/30 bg-green-500/5"
                    : "border-red-500/30 bg-red-500/5"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-semibold text-white">{item.prompt}</p>
                    <p
                      className={`mt-2 text-sm ${
                        item.skipped
                          ? "text-white/50"
                          : item.isCorrect
                          ? "text-green-300"
                          : "text-red-300"
                      }`}
                    >
                      {item.skipped
                        ? "You skipped this one"
                        : `You picked: ${item.userText}`}
                    </p>
                    {!item.isCorrect && !item.skipped && item.correctText && (
                      <p className="mt-1 text-sm text-emerald-300/80">
                        ✓ Correct: {item.correctText}
                      </p>
                    )}
                  </div>
                  <div className="text-2xl font-bold flex-shrink-0">
                    {item.skipped ? (
                      <span className="text-white/40">–</span>
                    ) : item.isCorrect ? (
                      <span className="text-green-400">✓</span>
                    ) : (
                      <span className="text-red-400">✗</span>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <ReportQuestionButton questionId={item.id} questionPrompt={item.prompt} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {relatedSuggestions.length > 0 && (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <p className="text-sm uppercase tracking-[0.3em] text-white/60">More quizzes</p>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {relatedSuggestions.map((item) => (
              <QuizCard key={item.id} quiz={item} />
            ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ResultPanel;
